import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Checkbox, FormControlLabel, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const CompanyEditModal = ({ open, onClose, company, onSave }) => {
    const [formData, setFormData] = useState(company || {});
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const originalDataRef = useRef(company);

    useEffect(() => {
        if (company) {
            setFormData(company);
            setImagePreview(company.CompanyLogoURL);
            originalDataRef.current = company;
        }
    }, [company]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
                setFormData({ ...formData, CompanyLogoURL: file });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleClearImage = () => {
        setImagePreview(null);
        setFormData({ ...formData, CompanyLogoURL: null });
    };

    const handleSubmit = async () => {
        setLoading(true);

        const updatedData = { CompanyID: formData.CompanyID };
        for (const key in formData) {
            if (formData[key] !== originalDataRef.current[key]) {
                updatedData[key] = formData[key];
            }
        }
        await onSave(updatedData);
        setLoading(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{company ? 'Edit Company' : 'Add Company'}</DialogTitle>
            <DialogContent>
                <TextField
                    label="Company Name"
                    name="CompanyName"
                    value={formData.CompanyName || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Company Type"
                    name="CompanyType"
                    value={formData.CompanyType || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Company Industry"
                    name="CompanyIndustry"
                    value={formData.CompanyIndustry || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Company Headquarters"
                    name="CompanyHeadquarters"
                    value={formData.CompanyHeadquarters || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Founded Year"
                    name="CompanyFoundedYear"
                    type="number"
                    value={formData.CompanyFoundedYear || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Revenue"
                    name="CompanyRevenue"
                    value={formData.CompanyRevenue || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Number of Employees"
                    name="CompanyNumberOfEmployees"
                    type="number"
                    value={formData.CompanyNumberOfEmployees || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Market Segment"
                    name="CompanyMarketSegment"
                    value={formData.CompanyMarketSegment || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="CompanyGlobalPresence"
                            checked={formData.CompanyGlobalPresence || false}
                            onChange={handleChange}
                        />
                    }
                    label="Global Presence"
                />
                <TextField
                    label="Website URL"
                    name="CompanyWebsiteURL"
                    value={formData.CompanyWebsiteURL || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Contact Info"
                    name="CompanyContactInfo"
                    value={formData.CompanyContactInfo || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                < TextField
                    label="Description"
                    name="CompanyDescription"
                    value={formData.CompanyDescription || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={4}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="CompanyIsConsultingCompany"
                            checked={formData.CompanyIsConsultingCompany || false}
                            onChange={handleChange}
                        />
                    }
                    label="Is Consulting Company"
                />
                {formData.CompanyIsConsultingCompany && (
                    <TextField
                        label="Consulting Company ID"
                        name="ConsultingCompanyID"
                        value={formData.ConsultingCompanyID || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                    />
                )}
                <Box marginY={2}>
                    <Typography variant="subtitle1">Update Company Logo</Typography>
                    <input
                        accept="image/*"
                        type="file"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                        id="upload-image"
                    />
                    <label htmlFor="upload-image">
                        <Button variant="contained" component="span">
                            Upload Image
                        </Button>
                    </label>
                    {imagePreview && (
                        <Box marginTop={2}>
                            <Typography variant="body2">Image Preview:</Typography>
                            <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100px', marginTop: '8px', marginLeft: '8px' }} />
                            <IconButton onClick={handleClearImage} aria-label="clear image" style={{ marginLeft: '8px', top: 0 }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button onClick={handleSubmit} color="primary" disabled={loading}>
                    {loading ? (
                        <>
                            <CircularProgress size={20} style={{ marginRight: 8 }} color="inherit" />
                            Updating...
                        </>
                    ) : (
                        "Update"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CompanyEditModal;